import type { ParseKeys } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { EmptyTileState } from '@/components/Dashboard/EmptyTileState';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Calendar } from '@/components/Icons/Calendar';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import { Modal } from '@/components/Modal';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { formatPeriodRange, isSamePeriod } from '@/pages/Overview/period';
import { CustomPeriodCard } from '@/pages/Overview/CustomPeriodCard';
import { RatingsOptionCard } from '@/pages/Overview/RatingsOptionCard';
import { SameLevelClassChips } from '@/pages/Overview/SameLevelClassChips';
import { useLessonTypes } from '@/pages/Overview/hooks/useLessonTypes';
import { useStudyYearPeriod } from '@/pages/Overview/hooks/useStudyYearPeriod';
import { useStudyYearPeriods } from '@/pages/Overview/hooks/useStudyYearPeriods';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { StudyYearPeriodType, type StudyYearPeriod } from '@/types/overview';
import {
  BorderRadius,
  ButtonVariant,
  EvaluationMode,
  ModalSize,
  ModalVariant,
  Size,
} from '@/types/components';
import { tv } from '@/utils/tv';

export interface RatingsFilterValues {
  mode: EvaluationMode;
  lessonTypeId: number | null;
  period: StudyYearPeriod;
  includeSameLevelClasses: boolean;
  sameLevelClassIds: number[];
}

interface Props {
  values: RatingsFilterValues;
  onClose: () => void;
  onApply: (values: RatingsFilterValues) => void;
}

const COMPARE_OPTIONS: Array<{
  includeSameLevelClasses: boolean;
  labelKey: ParseKeys;
}> = [
  {
    includeSameLevelClasses: false,
    labelKey: 'overview.ratings.compareClassmates',
  },
  {
    includeSameLevelClasses: true,
    labelKey: 'overview.ratings.compareParallel',
  },
];

const segment = tv({
  base: 'flex flex-1 cursor-pointer items-center justify-center rounded-[20px] text-subhead-14 transition-colors',
  variants: {
    isSelected: {
      true: 'bg-primary font-medium text-primary-foreground shadow-[0_1px_4px_0_rgba(0,0,0,0.1)]',
      false: 'text-foreground',
    },
  },
});

const fieldLabel = tv({
  base: 'flex-1 truncate text-left text-subhead-14',
  variants: {
    hasValue: {
      true: 'text-foreground',
      false: 'text-muted-foreground',
    },
  },
});

export const RatingsFilterSheet: React.FC<Props> = ({
  values,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation();
  const { periods } = useStudyYearPeriods();
  const studyYearPeriod = useStudyYearPeriod();
  const [view, setView] = useState<'main' | 'lessonTypes'>('main');
  const [pending, setPending] = useState<RatingsFilterValues>(values);
  const { sheetRef, swipeProps } = useSwipeToClose(onClose);
  const { data: lessonTypes = [], isLoading } = useLessonTypes({
    enabled: view === 'lessonTypes',
  });

  const isFinal = pending.mode === EvaluationMode.Final;
  const isCustomPeriod = pending.period.type === StudyYearPeriodType.Custom;
  const selectedTypeName = lessonTypes.find(
    (type) => type.id === pending.lessonTypeId,
  )?.name;

  const updatePending = (changes: Partial<RatingsFilterValues>) =>
    setPending((prev) => ({ ...prev, ...changes }));

  const handleLessonTypeSelect = (lessonTypeId: number) =>
    updatePending({ lessonTypeId, mode: EvaluationMode.Final });

  const handleSave = () => onApply(pending);

  return (
    <Modal
      isOpen
      label={t('overview.ratings.filterTitle')}
      variant={ModalVariant.BottomSheet}
      size={ModalSize.Small}
      className="max-h-[93svh]"
      contentRef={sheetRef}
      onClose={onClose}
    >
      <div
        {...swipeProps}
        className="flex shrink-0 cursor-grab justify-center pt-2 pb-1 touch-none active:cursor-grabbing"
      >
        <div className="h-1 w-10 rounded-full bg-drag-handle" />
      </div>
      <div className="flex shrink-0 items-center gap-2 px-4 pt-3 pb-3">
        {view === 'lessonTypes' && (
          <button
            type="button"
            onClick={() => setView('main')}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
          >
            <ArrowLeft className="size-6" />
          </button>
        )}
        <h2 className="text-[24px]/[30px] font-semibold text-foreground">
          {view === 'main'
            ? t('overview.ratings.filterTitle')
            : t('overview.ratings.evaluationType')}
        </h2>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-4">
        {view === 'main' && (
          <div key="main" className="flex animate-zoom-in flex-col">
            <div className="mb-6 flex h-10 rounded-[20px] bg-popover p-0.5 shadow-card">
              <button
                type="button"
                onClick={() => updatePending({ mode: EvaluationMode.Average })}
                className={segment({ isSelected: !isFinal })}
              >
                {t('overview.ratings.average')}
              </button>
              <button
                type="button"
                onClick={() => updatePending({ mode: EvaluationMode.Final })}
                className={segment({ isSelected: isFinal })}
              >
                {t('overview.ratings.final')}
              </button>
            </div>

            <div
              className={`grid transition-[grid-template-rows] duration-300 ease-out ${isFinal ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="overflow-hidden">
                <section className="flex flex-col gap-2 pb-6">
                  <h3 className="text-subhead-14 text-muted-foreground">
                    {t('overview.ratings.evaluationType')}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setView('lessonTypes')}
                    className="flex cursor-pointer items-center gap-3 rounded-xl bg-popover px-4 py-3 shadow-card"
                  >
                    <span className="flex items-center justify-center rounded-full bg-info-surface p-2 text-primary">
                      <Calendar className="size-4" />
                    </span>
                    <span
                      className={fieldLabel({ hasValue: !!selectedTypeName })}
                    >
                      {selectedTypeName ?? t('overview.ratings.chooseType')}
                    </span>
                    <ChevronDown className="size-4 shrink-0 text-primary" />
                  </button>
                </section>
              </div>
            </div>

            <section className="mb-6 flex flex-col gap-2">
              <h3 className="text-subhead-14 text-muted-foreground">
                {t('overview.ratings.period')}
              </h3>
              <div className="flex flex-col gap-2">
                {periods.map((period) => (
                  <RatingsOptionCard
                    key={period.name}
                    title={period.name}
                    subtitle={formatPeriodRange(period)}
                    hasIcon
                    isSelected={isSamePeriod(period, pending.period)}
                    onSelect={() => updatePending({ period })}
                  />
                ))}
                <CustomPeriodCard
                  studyYearPeriod={studyYearPeriod}
                  value={isCustomPeriod ? pending.period : null}
                  onChange={(period) => updatePending({ period })}
                />
              </div>
            </section>

            <section className="flex flex-col gap-2">
              <h3 className="text-subhead-14 text-muted-foreground">
                {t('overview.ratings.compare')}
              </h3>
              <div className="flex flex-col gap-2">
                {COMPARE_OPTIONS.map((option) => (
                  <RatingsOptionCard
                    key={option.labelKey}
                    title={t(option.labelKey)}
                    isSelected={
                      option.includeSameLevelClasses ===
                      pending.includeSameLevelClasses
                    }
                    onSelect={() =>
                      updatePending({
                        includeSameLevelClasses: option.includeSameLevelClasses,
                      })
                    }
                  />
                ))}
                <SameLevelClassChips
                  isVisible={pending.includeSameLevelClasses}
                  className="-mt-2"
                  value={pending.sameLevelClassIds}
                  onChange={(sameLevelClassIds) =>
                    updatePending({ sameLevelClassIds })
                  }
                />
              </div>
            </section>
          </div>
        )}

        {view === 'lessonTypes' && (
          <div
            key="lessonTypes"
            className="flex animate-zoom-in flex-col gap-2"
          >
            {isLoading && (
              <div className="flex flex-1 items-center justify-center py-8">
                <PageSpinner className="size-[20px] text-subtle-foreground" />
              </div>
            )}
            {!isLoading && lessonTypes.length === 0 && <EmptyTileState />}
            {!isLoading &&
              lessonTypes.map((type) => (
                <RatingsOptionCard
                  key={type.id}
                  title={type.name}
                  hasIcon
                  isSelected={pending.lessonTypeId === type.id}
                  onSelect={() => handleLessonTypeSelect(type.id)}
                />
              ))}
          </div>
        )}
      </div>

      <div className="shrink-0 px-4 pt-3 pb-[calc(16px+env(safe-area-inset-bottom))]">
        <Button
          variant={ButtonVariant.Primary}
          size={Size.Medium}
          radius={BorderRadius.Small}
          className="h-14 w-full font-semibold"
          onClick={handleSave}
        >
          {t('overview.ratings.save')}
        </Button>
      </div>
    </Modal>
  );
};
